import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Button, Card, CardContent, ProfileSkeleton, Skeleton } from '@/ui/components'
import { EmptyState } from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { User } from '@/ui/icons'
import { useAuth } from '@/modules/auth'
import { UserProfileApi, UserProfile as UserProfileType } from '@/shared/api/users'
import {
  UserProfileHeader,
  UserStatsSection,
  ContributionGraph,
  PreferencesSection
} from '@/modules/users/components'
import { useUserContributions } from '@/modules/users/hooks/useUserContributions'
import { useUserDisplayName } from '@/modules/users/hooks/useUserDisplayName'
import { GarageLinkCard } from '@/modules/vehicles/components'
import { useVehicles } from '@/modules/vehicles/hooks/useVehicles'
import { useDashboard } from '@/modules/dashboard'

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>()
  const { user, getAccessToken } = useAuth()
  const { dashboardData } = useDashboard()

  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isOwnProfile = user?.id === userId

  // Get contributions - MUST be called before any early returns (Rules of Hooks)
  const { contributions, isLoading: isLoadingContributions } = useUserContributions(
    userId || ''
  )
  const { vehicles, primaryVehicle } = useVehicles(userId)
  const { displayName } = useUserDisplayName(isOwnProfile ? undefined : userId)

  useEffect(() => {
    if (!userId) return

    const loadProfile = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const userProfileApi = new UserProfileApi(getAccessToken)
        setUserProfile(await userProfileApi.getUserProfile(userId))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile')
      } finally {
        setIsLoading(false)
      }
    }

    loadProfile()
  }, [userId, getAccessToken])

  if (isLoading) {
    return <ProfileSkeleton />
  }

  if (error || !userProfile) {
    return (
      <PageLayout>
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <EmptyState
            icon={<User className="mx-auto h-8 w-8 text-earth-500" aria-hidden />}
            title="This profile didn't load"
            description={error || 'Profile not found'}
            action={{ label: 'Try again', onClick: () => window.location.reload() }}
          />
        </div>
      </PageLayout>
    )
  }

  const userName = isOwnProfile
    ? user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'You'
    : displayName || 'SitRepp member'

  const recentTrips = isOwnProfile ? (dashboardData?.recentTrips?.slice(0, 3) || []) : []
  const showSideColumn = isOwnProfile || !!primaryVehicle

  return (
    <PageLayout>
      <div className="min-h-screen bg-background pb-24 md:pb-10">
        <UserProfileHeader
          profile={userProfile}
          userName={userName}
          isOwnProfile={isOwnProfile}
          onProfileUpdate={setUserProfile}
        />

        <div className="mx-auto mt-8 w-full max-w-6xl space-y-4 px-4 md:px-6">
          {isOwnProfile && (
            <Card className="border-border bg-card shadow-none">
              <CardContent className="p-5">
                <h2 className="mb-4 text-base font-semibold text-foreground">Contributions</h2>
                {isLoadingContributions ? (
                  <div className="space-y-3" aria-hidden>
                    <Skeleton className="h-8 w-full rounded-md" />
                    <Skeleton className="h-32 w-full rounded-md" />
                  </div>
                ) : (
                  <ContributionGraph contributions={contributions} />
                )}
              </CardContent>
            </Card>
          )}

          <div className={showSideColumn ? 'grid items-start gap-4 lg:grid-cols-3' : undefined}>
            <div className={showSideColumn ? 'lg:col-span-2' : undefined}>
              <UserStatsSection userId={userId!} />
            </div>

            {showSideColumn && (
              <div className="space-y-4">
                {primaryVehicle && (
                  <GarageLinkCard
                    ownerId={userId!}
                    primaryVehicle={primaryVehicle}
                    vehicleCount={vehicles.length}
                  />
                )}

                {recentTrips.length > 0 && (
                  <Card className="border-border bg-card shadow-none">
                    <CardContent className="p-5">
                      <h2 className="text-base font-semibold text-foreground">Recent trips</h2>
                      <ul className="mt-2 divide-y divide-border">
                        {recentTrips.map((trip) => (
                          <li key={trip.id}>
                            <Link
                              to={`/trips/${trip.id}`}
                              className="-mx-2 block rounded-md px-2 py-2.5 transition-colors hover:bg-earth-50"
                            >
                              <p className="truncate text-sm font-medium text-foreground">{trip.name}</p>
                              <p className="mt-0.5 text-sm text-earth-600">
                                {[
                                  trip.start_date && new Date(trip.start_date).toLocaleDateString(),
                                  `${trip.member_count || 0} ${(trip.member_count || 0) === 1 ? 'member' : 'members'}`,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Button variant="outline" size="sm" asChild className="mt-3 w-full">
                        <Link to="/trips">View all trips</Link>
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {isOwnProfile && <PreferencesSection />}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
